import { Controller } from '@hotwired/stimulus';

// Connects to data-controller="hubspot"
//
// HubSpot tracking integration for a Turbo-driven app.
//
// Behavior contract (matches HubSpot's tracking-code API docs):
//   1. The `hs-script-loader` <script> in <head> loads ONCE per session
//      (Turbo's head-merge keeps it from re-fetching across navigations).
//   2. When the loader first executes, it auto-fires a single `trackPageView`
//      using whatever was already pushed onto `_hsq` (setPath, identify).
//   3. For every SUBSEQUENT Turbo navigation, we must manually push:
//        setPath  -> identify (if user) -> trackPageView
//      in that order. `setPath` MUST precede `trackPageView`, and `identify`
//      only takes effect when followed by a tracking call.
//
// We therefore skip the manual `trackPageView` on the very first `connect`
// (the loader's auto-pageview covers it) and fire it on every connect after.
//
// The `initialized` flag is module-scoped so it survives Turbo navigations
// but resets on a full document reload (which is exactly what we want, since
// a full reload re-runs the loader and triggers another auto-pageview).

let initialized = false;

export default class extends Controller {
  static values = {
    email: String,
    firstName: String,
    lastName: String,
  };

  connect() {
    const _hsq = (window._hsq = window._hsq || []);
    const phase = initialized ? 'turbo-navigation' : 'initial-load';
    console.log(`[HubSpot] connect() — phase=${phase}`, {
      path: this.currentPath(),
      hasUser: this.hasEmailValue && !!this.emailValue,
      hsqLength: _hsq.length,
    });

    // Always reflect the current URL on every page view.
    const path = this.currentPath();
    _hsq.push(['setPath', path]);
    console.log('[HubSpot] pushed setPath:', path);

    // Re-identify on every page so the tracker has fresh identity before
    // any subsequent tracking call. Pushing repeatedly is safe and idempotent.
    if (this.hasEmailValue && this.emailValue) {
      const identity = {
        email: this.emailValue,
        firstname: this.firstNameValue,
        lastname: this.lastNameValue,
      };
      _hsq.push(['identify', identity]);
      console.log('[HubSpot] pushed identify:', identity);
    } else {
      console.log('[HubSpot] skipped identify — no signed-in user');
    }

    if (initialized) {
      // Subsequent Turbo navigation: loader won't auto-track, so we must.
      _hsq.push(['trackPageView']);
      console.log('[HubSpot] pushed trackPageView (manual, Turbo nav)');
    } else {
      // Initial document load: the loader's auto-pageview will fire and pick
      // up the setPath/identify we just queued. Don't double-count.
      initialized = true;
      console.log(
        '[HubSpot] skipped trackPageView on initial load — loader will auto-fire it with queued setPath/identify',
      );
    }

    console.log('[HubSpot] _hsq after connect:', _hsq.length, 'entries');
  }

  disconnect() {
    console.log('[HubSpot] disconnect() — controller detached');
  }

  currentPath() {
    return window.location.pathname + window.location.search;
  }
}
